import { CategoriesTreeLevel, CategoryWithHeight } from "@features/graph/GraphCategories"
import { GraphNodeEdgeData } from "@store/services/converter/types"
import logger from "@utils/logger"

export const getOrderedCategories = (
  categories: CategoryWithHeight[],
  columnHeight: number,
  gapSize: number,
) => {
  const sortedCategories = categories.toSorted(
    (catA, catB) => catB.totalNodesCount - catA.totalNodesCount,
  )
  const availableCategories = new Map(
    sortedCategories.map((category) => [category.groupId, category]),
  )
  const ordered: CategoryWithHeight[][] = []
  let columnIndex = 0

  while (availableCategories.size > 0) {
    const columnCategories: CategoryWithHeight[] = []
    ordered[columnIndex] = columnCategories

    let availableColumnHeight = columnHeight

    availableCategories.forEach((category) => {
      if (availableColumnHeight >= category.height) {
        availableCategories.delete(category.groupId)
        availableColumnHeight -= category.height
        columnCategories.push(category)

        if (columnCategories.length > 1) {
          availableColumnHeight -= gapSize
        }
      }
    })

    // fallback to avoid infinite loop, drop left categories to the last column
    if (columnIndex > 100) {
      ordered.push(Array.from(availableCategories.values()))
      return ordered
    }

    columnIndex++
  }

  return ordered
}

export const createCategoriesTree = (
  groupMap: GraphNodeEdgeData["groupMap"],
  parentId?: string,
): CategoriesTreeLevel[] => {
  const groups = Object.values(groupMap).filter((group) => group.group.parent === parentId)

  if (!parentId)
    return [
      {
        groupId: "root",
        parentId: null,
        label: "root",
        totalNodesCount: 0,
        color: "",
        groups: groups.map((group) => ({
          groupId: group.id,
          parentId: "root",
          label: group.group.label,
          totalNodesCount: group.group.nodes.length,
          color: group.group.color,
          groups: createCategoriesTree(groupMap, group.id),
        })),
      },
    ]

  return groups.map((group) => ({
    groupId: group.id,
    parentId: parentId,
    label: group.group.label,
    totalNodesCount: group.group.nodes.length,
    color: group.group.color,
    groups: createCategoriesTree(groupMap, group.id),
  }))
}

export const findCategoryLevel = (
  tree: CategoriesTreeLevel[],
  categoryId: string,
): CategoriesTreeLevel | undefined => {
  const topLevelCategory = tree.find((level) => level.groupId === categoryId)

  if (topLevelCategory) return topLevelCategory

  for (const level of tree) {
    const foundInChild = findCategoryLevel(level.groups, categoryId)
    if (foundInChild) return foundInChild
  }
}

export const getCategoryLevelPath = (
  categoryId: string,
  tree: CategoriesTreeLevel[],
): CategoriesTreeLevel[] => {
  const categoryLevel = findCategoryLevel(tree, categoryId)

  if (!categoryLevel) {
    logger.error(`Category with id "${categoryId}" not found in tree`)
    return []
  }

  const path: CategoriesTreeLevel[] = [categoryLevel]
  if (!categoryLevel.parentId) return path

  let parentCategory = findCategoryLevel(tree, categoryLevel.parentId)
  do {
    path.push(parentCategory!)
    parentCategory = parentCategory!.parentId
      ? findCategoryLevel(tree, parentCategory!.parentId)
      : undefined
  } while (parentCategory)

  return path.toReversed()
}
